import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { patchVelocityParameter } from 'Services';
import { RQ_PARAMETERS, RQ_PARAMETER_BY_ID } from 'Utils';
import { FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';
import { ReactQueryError } from '@brighterion/types/react-query';

const usePatchVelocityParameter = (formRef: React.RefObject<FormikProps<ParameterVelocityFormSchema>>) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { push } = useHistory();
    const { addAlert } = useAlertBanner();
    const { addModal, addTechnicalErrorModal } = useReduxModal();

    const onSuccess = async (_data: any, variables: Partial<PatchVelocityParameterRequest>) => {
        addAlert({
            id: 'patch-velocity-parameter',
            content: t('CreateVelocityParameterSuccessModalText', {
                displayName: variables.message,
                status: variables.status
            }),
            status: 'success'
        });

        queryClient.invalidateQueries([RQ_PARAMETERS]);
        push(ROUTES.PARAMETERS.INDEX);
    };

    const onError = (error: ReactQueryError, variables: Partial<PatchVelocityParameterRequest>) => {
        const errorData = error.response.data;

        switch (errorData.statusCode) {
            case 400:
                addModal({
                    id: 'patch-velocity-parameter-400-error',
                    status: 'error',
                    title: t('Parameter could not be saved'),
                    content: (
                        <Trans
                            i18nKey='createVelocity400Error'
                            values={{
                                displayName: variables.fieldName,
                                errorMessage: errorData.error?.message ?? errorData.message
                            }}
                        />
                    ),
                    cancelText: t('Close')
                });

                break;
            default:
                addTechnicalErrorModal();
                break;
        }

        // retain user's values
        formRef.current?.resetForm({
            values: formRef.current.values,
            touched: formRef.current.touched,
            errors: formRef.current.errors,
            isSubmitting: false
        });
    };

    return useMutation([RQ_PARAMETER_BY_ID], patchVelocityParameter, {
        onSuccess,
        onError
    });
};

export default usePatchVelocityParameter;
